import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Snowfall from "react-snowfall";

import Navbar from "./components/navbar";
import Footer from "./components/footer";
import Splash from "./components/Splash";
import ScrollTopButton from "./components/ScrollTopButton";

import History from "./pages/info/History";
import Home from "./pages/home";
import WhoWeAre from "./pages/info/WhoWeAre";
import PrayerLiturgy from "./pages/info/PrayerLiturgy";
import Announcements from "./pages/info/Announcements";
import Events from "./pages/info/Events";
import Contact from "./pages/info/Contact";
import Religious from "./pages/info/Religious";
import VillaTheresa from "./pages/info/VillaTheresa";
import SophiaCollege from "./pages/info/SophiaCollege";
import StElizabeth from "./pages/info/StElizabeth";
import Communities from "./pages/info/communities";
import Clergy from "./pages/info/clergy";
import CellsAssociations from "./pages/info/cellsAssociations";
import Gallery from "./pages/info/gallery";
import PopupNotice from "./pages/info/Popupnotice";

let appInitialized = false;

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    if (prevPathRef.current !== pathname) {
      prevPathRef.current = pathname;
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return null;
}

function isChristmasSeason() {
  const today = new Date();
  const month = today.getMonth();
  const date = today.getDate();
  if (month === 11) return true;
  if (month === 0 && date <= 7) return true;
  return false;
}

function AppContent() {
  const [showSplash, setShowSplash] = useState(!appInitialized);
  // ✅ Popup only activates after splash is done
  const [splashDone, setSplashDone] = useState(appInitialized);
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  const navigate = useNavigate();
  const shouldRedirectRef = useRef(false);

  useEffect(() => {
    if (!appInitialized) {
      appInitialized = true;
      setShowSplash(true);
      setSplashDone(false);
      if (location.pathname !== "/") {
        shouldRedirectRef.current = true;
      }
      window.scrollTo(0, 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showSplash && shouldRedirectRef.current) {
      shouldRedirectRef.current = false;
      navigate("/", { replace: true });
      window.scrollTo(0, 0);
    }
    // ✅ When splash finishes, enable the popup
    if (!showSplash) {
      setSplashDone(true);
    }
  }, [showSplash, navigate]);

  useEffect(() => {
    if (prevLocationRef.current !== location.pathname) {
      prevLocationRef.current = location.pathname;
      const siteRoot = document.getElementById("site-root");
      const navbar = document.querySelector("nav");
      if (siteRoot) {
        siteRoot.style.visibility = "visible";
        siteRoot.style.opacity = "1";
        siteRoot.style.transform = "translateY(0)";
      }
      if (navbar) {
        navbar.style.visibility = "visible";
        navbar.style.opacity = "1";
      }
    }
  }, [location.pathname]);

  return (
    <>
      {/* ❄️ Snow layers */}
      {isChristmasSeason() && (
        <Snowfall snowflakeCount={35} speed={[0.1, 0.35]} radius={[0.6, 1.2]} wind={[-0.05, 0.05]} color="#DCEEFF"
          style={{ position: "fixed", inset: 0, zIndex: 996099, opacity: 0.25, pointerEvents: "none" }} />
      )}
      {isChristmasSeason() && (
        <Snowfall snowflakeCount={40} speed={[0.25, 0.8]} radius={[1.4, 2.6]} wind={[-0.5, 0.5]} color="#DCEEFF"
          style={{ position: "fixed", inset: 0, zIndex: 9980999, opacity: 0.55, pointerEvents: "none" }} />
      )}
      {isChristmasSeason() && (
        <Snowfall snowflakeCount={25} speed={[0.6, 1.4]} radius={[2.2, 3.8]} wind={[-0.74, 0.71]} color="#DCEEFF"
          style={{ position: "fixed", inset: 0, zIndex: 99999999, opacity: 0.9, pointerEvents: "none" }} />
      )}

      {/* Splash screen — passes onDone so we know exactly when it finishes */}
      {showSplash && (
        <Splash
          showOnce={false}
          minVisible={3500}
          maxTimeout={5000}
          onDone={() => setShowSplash(false)}
        />
      )}

      {/* ✅ Popup only mounts after splash is completely done */}
      {splashDone && <PopupNotice />}

      <ScrollToTop />

      <div className="flex flex-col min-h-screen">
        <Navbar />
        <div id="site-root" className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<WhoWeAre />} />
            <Route path="/prayer" element={<PrayerLiturgy />} />
            <Route path="/history" element={<History />} />
            <Route path="/religious" element={<Religious />} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/events" element={<Events />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/religious/villa-theresa" element={<VillaTheresa />} />
            <Route path="/religious/sophia-college" element={<SophiaCollege />} />
            <Route path="/religious/st-elizabeth-hospital" element={<StElizabeth />} />
            <Route path="/parish/communities" element={<Communities />} />
            <Route path="/parish/clergy" element={<Clergy />} />
            <Route path="/parish/cells" element={<CellsAssociations />} />
            <Route path="/parish/gallery" element={<Gallery />} />
          </Routes>
        </div>
        <Footer />
        <ScrollTopButton />
      </div>
    </>
  );
}

export default function App() {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => { appInitialized = false; };
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}